import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Activity, Box, ChevronDown, ShieldAlert, Ban, Building2, Factory, PencilRuler, ShieldCheck, Truck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useProject } from "@/pages/project/ProjectLayout";
import { useProjectSocket } from "@/hooks/useProjectSocket";
import { DEPARTMENTS, statusColor } from "@/constants/workflow";
import { useCountUp } from "@/hooks/useCountUp";

function Metric({
  label,
  value,
  icon: Icon,
  tone = "cyan",
  testId,
  pulse = false,
  glow = false,
  popOnIncrease = false,
}) {
  const [popKey, setPopKey] = useState(0);
  const prevValue = useRef(value);
  useEffect(() => {
    if (popOnIncrease && value > prevValue.current) setPopKey((key) => key + 1);
    prevValue.current = value;
  }, [value, popOnIncrease]);
  const tones = {
    cyan: "text-cyan-700 bg-cyan-500/10",
    amber: "text-amber-700 bg-amber-500/10",
    red: "text-red-700 bg-red-500/10",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div
      data-testid={testId}
      className="okn-rise flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
    >
      <div
        className={`grid h-8 w-8 shrink-0 place-items-center rounded-md ${tones[tone]} ${
          pulse ? "okn-pulse-ring" : ""
        } ${glow ? "okn-glow-red" : ""}`}
        data-testid={pulse ? `${testId}-pulse` : glow ? `${testId}-glow` : undefined}
      >
        <span key={popKey} className={popKey ? "okn-pop-once" : undefined}>
          <Icon size={15} />
        </span>
      </div>
      <div className="flex min-w-0 items-baseline gap-2 max-sm:flex-col max-sm:items-start max-sm:gap-0.5">
        <p className="mono text-xl font-medium leading-none tracking-tight">{value}</p>
        <p className="text-xs uppercase leading-tight tracking-wider text-slate-500 sm:truncate max-sm:text-[9.5px]">
          {label}
        </p>
      </div>
    </div>
  );
}

// Key "done" status per department — drives the progress percentage on each card.
const KEY_STATUS = {
  design: "Approved",
  bim: "Published",
  production: "Completed",
  qaqc: "Passed",
  logistics: "Arrived",
  site: "Installed",
};


// Mini icon + a short, related loop animation per department.
const DEPT_ICON = {
  design: { icon: PencilRuler, anim: "okn-tilt" },
  bim: { icon: Box, anim: "okn-float" },
  production: { icon: Factory, anim: "okn-blink" },
  qaqc: { icon: ShieldCheck, anim: "okn-breathe" },
  logistics: { icon: Truck, anim: "okn-truck-drive" },
  site: { icon: Building2, anim: "okn-float" },
};

function InstallVelocity({ data, projectId, onBucketClick }) {
  const [granularity, setGranularity] = useState("week");
  const [rate, setRate] = useState(data);

  useEffect(() => {
    if (granularity === "week" && rate === data) return undefined;
    let alive = true;
    api
      .get(`/projects/${projectId}/overview/install-rate`, { params: { granularity } })
      .then((res) => alive && setRate(res.data))
      .catch((error) => toast.error(apiError(error)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularity, projectId]);

  const buckets = rate?.buckets || rate?.weeks || [];
  if (!buckets.length) return null;
  const peak = Math.max(rate.peak || 0, 1);
  const filled = buckets.filter((item) => item.panels > 0);
  const groupKey = (iso) => {
    const value = new Date(iso);
    if (granularity === "month") return String(value.getUTCFullYear());
    if (granularity === "week") return iso.slice(0, 7);
    // Daily view groups by ISO week (Monday start).
    const monday = new Date(value);
    monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
    return monday.toISOString().slice(0, 10);
  };
  const fmt = (iso) => {
    const value = new Date(iso);
    if (granularity === "month")
      return value.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    return value.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <section className="mt-7" data-testid="install-velocity-section">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <h2 className="font-display text-base font-medium tracking-tight md:text-lg">
          Installation Rate
        </h2>
        <p className="mono text-[11px] uppercase tracking-wider text-slate-500">
          {rate.installed_total} installed · avg {rate.average}/{granularity}
        </p>
        <div className="ml-auto flex gap-1">
          {["day", "week", "month"].map((value) => (
            <button
              key={value}
              data-testid={`install-granularity-${value}`}
              onClick={() => setGranularity(value)}
              className={`mono rounded-full px-3 py-1 text-[10px] uppercase tracking-wider transition-colors ${
                granularity === value
                  ? "bg-cyan-600/15 text-cyan-700"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-100"
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="okn-scroll max-h-80 space-y-1.5 overflow-y-auto">
          {filled.map((item, index) => {
            const key = item.bucket || item.week_start;
            const previous = filled[index - 1];
            const newGroup =
              index > 0 && groupKey(key) !== groupKey(previous.bucket || previous.week_start);
            return (
              <div key={key}>
                {newGroup ? (
                  <div
                    data-testid={`install-divider-${key}`}
                    className="mx-1.5 my-1.5 border-t border-slate-200"
                  />
                ) : null}
                <button
                  data-testid={`install-bucket-${key}`}
                  onClick={() => onBucketClick(item)}
                  title={`${item.panels} panel(s) installed · click to select them in the model`}
                  className="group flex w-full items-center gap-3 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-[#FF2D95]/8"
                >
                  <span className="mono w-28 shrink-0 whitespace-nowrap text-[10.5px] text-slate-600">
                    {fmt(key)}
                  </span>
                  <span className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <span
                      className="okn-bar-grow block h-full rounded-full bg-emerald-500/85 transition-colors group-hover:bg-[#FF2D95]"
                      style={{ width: `${Math.max((item.panels / peak) * 100, 4)}%` }}
                    />
                  </span>
                  <span
                    data-testid={`install-bucket-count-${key}`}
                    className="mono w-8 shrink-0 text-right text-[11px] font-medium text-emerald-700 transition-colors group-hover:text-[#FF2D95]"
                  >
                    {item.panels}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DepartmentCard({ department, totalPanels, onNoteClick }) {
  // Always measure against the project's panel count: legacy/unknown status values are not
  // part of the buckets, so their sum can be smaller than the real total.
  const total = totalPanels || department.buckets.reduce((sum, b) => sum + b.count, 0) || 1;
  const keyStatus = KEY_STATUS[department.key];
  const done = keyStatus
    ? department.buckets.find((b) => b.status === keyStatus)?.count || 0
    : 0;
  const percent = Math.round((done / total) * 100);
  const shownPercent = useCountUp(percent);
  return (
    <div
      data-testid={`overview-dept-${department.key}`}
      className="rounded-xl border border-slate-200 bg-white p-4"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-display flex items-center gap-2 text-sm font-medium tracking-tight">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-cyan-500/10 text-cyan-700">
            <span
              className={DEPT_ICON[department.key]?.anim}
              data-testid={`overview-dept-icon-${department.key}`}
            >
              {(() => {
                const Icon = DEPT_ICON[department.key]?.icon || Box;
                return <Icon size={13} />;
              })()}
            </span>
          </span>
          {department.label}
        </h3>
        <span className="mono text-[11px] text-slate-500">{total}</span>
      </div>
      {keyStatus ? (
      <div className="mt-1 flex items-baseline gap-1.5">
        <span
          data-testid={`overview-${department.key}-percent`}
          className="mono text-lg font-medium"
          style={{ color: statusColor(department.key, keyStatus) }}
        >
          {shownPercent}%
        </span>
        <span className="mono text-[12px] uppercase tracking-wider text-slate-500">
          {keyStatus} · {done}/{total}
          {department.key === "site" && department.installed_area_m2 != null ? (
            <span data-testid="overview-site-area" className="normal-case">
              {" "}· {department.installed_area_m2.toLocaleString()} m²
            </span>
          ) : null}
        </span>
      </div>
      ) : null}
      <div className="okn-bar-grow mt-3 flex h-1.5 overflow-hidden rounded-full bg-slate-100">
        {department.buckets.map((bucket) => (
          <div
            key={bucket.status}
            style={{
              width: `${(bucket.count / total) * 100}%`,
              backgroundColor: statusColor(
                department.key,
                bucket.status === "—" ? null : bucket.status
              ),
            }}
          />
        ))}
      </div>
      <ul className="mt-3 space-y-1.5">
        {department.buckets.map((bucket) => (
          <li key={bucket.status} className="text-[13px]">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-slate-700">
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor: statusColor(
                    department.key,
                    bucket.status === "—" ? null : bucket.status
                  ),
                }}
              />
              {bucket.status}
            </span>
            <span
              className="mono text-slate-600"
              data-testid={`overview-${department.key}-${bucket.status.replace(/[^\w]/g, "-")}`}
            >
              {bucket.count}
            </span>
          </div>
          {bucket.notes?.length ? (
            <ul
              data-testid={`overview-notes-${department.key}-${bucket.status.replace(/[^\w]/g, "-")}`}
              className="ml-4 mt-1 list-disc space-y-0.5 pl-3 text-[11.5px] text-slate-600"
            >
              {bucket.notes.map((note) => (
                <li key={note.text}>
                  <button
                    type="button"
                    data-testid={`overview-note-${department.key}-${note.text
                      .slice(0, 24)
                      .replace(/[^\w]/g, "-")}`}
                    onClick={() => onNoteClick?.(department.key, bucket.status, note)}
                    className="text-left hover:text-cyan-700 hover:underline"
                    title="Show these panels in the 3D model"
                  >
                    {note.text}
                    {note.panels > 1 ? (
                      <span className="mono ml-1 text-[9.5px] text-slate-400">×{note.panels}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

const ACTIVITY_OPEN_KEY = "okn-activity-open";

/** Collapsible activity feed with a per-department filter. */
function ActivityFeed({ rows }) {
  const [open, setOpen] = useState(() => localStorage.getItem(ACTIVITY_OPEN_KEY) !== "0");
  const [department, setDepartment] = useState("all");

  const toggle = () => {
    setOpen((prev) => {
      localStorage.setItem(ACTIVITY_OPEN_KEY, prev ? "0" : "1");
      return !prev;
    });
  };

  const counts = rows.reduce((acc, row) => {
    acc[row.department] = (acc[row.department] || 0) + 1;
    return acc;
  }, {});
  const visible = department === "all" ? rows : rows.filter((r) => r.department === department);

  return (
    <section className="mt-5 pb-4" data-testid="recent-activity-section">
      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="activity-toggle"
          onClick={toggle}
          className="flex items-center gap-2 text-left"
        >
          <Activity size={15} className="text-cyan-600" />
          <h2 className="font-display text-base font-medium tracking-tight md:text-lg">
            Recent Activity
          </h2>
          <span className="mono rounded bg-slate-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-slate-600">
            {visible.length}
          </span>
          <ChevronDown
            size={14}
            className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open ? (
          <div className="ml-auto flex flex-wrap gap-1.5">
            {[{ key: "all", label: "All" }, ...DEPARTMENTS].map((dept) => (
              <button
                key={dept.key}
                data-testid={`activity-filter-${dept.key}`}
                onClick={() => setDepartment(dept.key)}
                className={`mono rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wider transition-colors ${
                  department === dept.key
                    ? "bg-cyan-500/18 text-cyan-700"
                    : "border border-slate-200 text-slate-500 hover:bg-slate-100"
                }`}
              >
                {dept.label}
                {dept.key !== "all" && counts[dept.key] ? ` ${counts[dept.key]}` : ""}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {open ? (
        <div className="mt-2 divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {visible.length === 0 ? (
            <p className="px-4 py-4 text-xs text-slate-500">
              No status changes recorded for this department yet.
            </p>
          ) : (
            visible.map((row, index) => (
              <div
                key={`${row.panel_uid}-${row.changed_at}-${index}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-xs"
                data-testid="activity-row"
              >
                <span className="mono text-[11px] text-cyan-700">{row.panel_name}</span>
                <span className="text-slate-500">{row.department_label}</span>
                <span className="text-slate-600">
                  {row.old_status || "—"} → <b className="text-slate-900">{row.new_status || "—"}</b>
                </span>
                <span className="mono ml-auto text-[10px] text-slate-500">
                  {row.changed_by} · {new Date(row.changed_at).toLocaleString()}
                </span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </section>
  );
}

export default function OverviewPage() {
  const { projectId } = useProject();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  // Action Required cards deep-link into the tab that can resolve them.
  const openAction = (item) => {
    if (item.label === "Open High Priority Issues") {
      navigate(`/projects/${projectId}/issues`);
      return;
    }
    if (item.label === "Shipments past their arrival date") {
      navigate(`/projects/${projectId}/logistics?tab=archive`);
      return;
    }
    if (item.department && item.status) {
      navigate(
        `/projects/${projectId}/model?status_${item.department}=${encodeURIComponent(item.status)}&select=1`,
      );
    }
  };

  // A reason under Workflow Status opens the Model tab with exactly those panels selected.
  const openNote = (department, status, note) => {
    if (!note.panel_uids?.length) return;
    navigate(
      `/projects/${projectId}/model?status_${department}=${encodeURIComponent(status)}`,
      { state: { uids: note.panel_uids } }
    );
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/overview`);
      setData(res.data);
    } catch (error) {
      toast.error(apiError(error));
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useProjectSocket(projectId, (message) => {
    if (
      ["panel_status_changed", "issue_created", "issue_updated", "panel_shipping_changed"].includes(
        message.event
      )
    )
      load();
  });

  if (!data)
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
      </div>
    );

  return (
    <main className="okn-scroll mx-auto w-full max-w-[1500px] flex-1 overflow-y-auto px-4 py-6 sm:px-6" data-testid="overview-page">
      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        <Metric label="Total panels" value={data.total_panels} icon={Box} testId="metric-total-panels" />
        <Metric
          label="Workflow blocked"
          value={data.blocked_panels}
          icon={Ban}
          tone={data.blocked_panels ? "red" : "slate"}
          pulse={data.blocked_panels > 0}
          testId="metric-blocked-panels"
        />
        <Metric
          label="Open issues"
          value={data.open_issues}
          icon={AlertTriangle}
          tone={data.open_issues ? "amber" : "slate"}
          popOnIncrease
          testId="metric-open-issues"
        />
        <Metric
          label="NCR records"
          value={data.ncr_count}
          icon={ShieldAlert}
          tone={data.ncr_count ? "red" : "slate"}
          glow={data.ncr_count > 0}
          testId="metric-ncr"
        />
      </div>

      <section className="mt-5" data-testid="action-required-section">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-base font-medium tracking-tight md:text-lg">
            Action Required
          </h2>
          <span className="mono rounded bg-slate-100 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-slate-600">
            {data.action_required.length} items
          </span>
        </div>
        {data.action_required.length === 0 ? (
          <p className="mt-2 rounded-lg border border-emerald-500/25 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700">
            No blocking conditions. All panels are progressing normally.
          </p>
        ) : (
          <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            {data.action_required.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => openAction(item)}
                data-testid={`action-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                  item.severity === "critical"
                    ? "border-red-500/30 bg-red-500/8 hover:border-red-500/60"
                    : "border-amber-500/30 bg-amber-500/8 hover:border-amber-500/60"
                }`}
              >
                <span className="text-xs leading-tight text-slate-800">{item.label}</span>
                <span
                  className={`mono text-base font-medium ${
                    item.severity === "critical" ? "text-red-700" : "text-amber-700"
                  }`}
                >
                  {item.count}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="mt-5">
        <h2 className="font-display text-base font-medium tracking-tight md:text-lg">
          Workflow Status
        </h2>        <div className="mt-2 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.departments.map((department) => (
            <DepartmentCard
              key={department.key}
              department={department}
              totalPanels={data.total_panels}
              onNoteClick={openNote}
            />
          ))}
        </div>
      </section>

      <InstallVelocity
        data={data.install_velocity}
        projectId={projectId}
        onBucketClick={(item) =>
          item.panel_uids?.length &&
          navigate(`/projects/${projectId}/model?status_site=Installed`, {
            state: { uids: item.panel_uids },
          })
        }
      />

      <ActivityFeed rows={data.recent_activity} />
    </main>
  );
}
